<div class="schedule-management-page">
  <div
    class="toast show"
    *ngIf="toastMessage"
    [class.success]="toastKind === 'success'"
    [class.error]="toastKind === 'error'"
    [class.warning]="toastKind === 'info'"
    (click)="clearToast()"
    role="status"
    aria-live="polite"
  >
    <img
      class="icon"
      [src]="toastKind === 'success' ? '/success.png' : toastKind === 'error' ? '/fail.png' : '/warning.png'"
      alt=""
      aria-hidden="true"
    />

    <div class="toast-title">
      {{ toastKind === 'success' ? 'Success' : toastKind === 'error' ? 'Action needed' : 'Notice' }}
    </div>

    <div class="toast-body">{{ toastMessage }}</div>
  </div>

  <header class="schedule-page-header">
    <div>
      <p class="eyebrow">Scheduling Management</p>
      <h1>Default Schedules</h1>
    </div>
  </header>

  <nav class="schedule-tabs" aria-label="Schedule sections">
    <a
      class="schedule-tab"
      routerLink="/scheduling/schedule-groups"
      routerLinkActive="active"
      [routerLinkActiveOptions]="{ exact: true }"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4" />
        <path d="M8 2v4" />
        <path d="M3 10h18" />
      </svg>
      Default Schedules
    </a>

    <a
      class="schedule-tab"
      routerLink="/scheduling/user-assign-schedules"
      routerLinkActive="active"
      [routerLinkActiveOptions]="{ exact: true }"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M8 7h12" />
        <path d="M8 12h12" />
        <path d="M8 17h12" />
        <path d="M4 7h.01" />
        <path d="M4 12h.01" />
        <path d="M4 17h.01" />
      </svg>
      Assign Schedules
    </a>

    <a
      class="schedule-tab"
      routerLink="/scheduling/scheduling-sync"
      routerLinkActive="active"
      [routerLinkActiveOptions]="{ exact: true }"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M21 12a9 9 0 0 1-9 9 8.5 8.5 0 0 1-6-2.5" />
        <path d="M3 12a9 9 0 0 1 15-6.7" />
        <path d="M18 3v5h-5" />
        <path d="M6 21v-5h5" />
      </svg>
      Sync Schedules
    </a>
  </nav>

  <div *ngIf="isSaving" class="busy-overlay" aria-live="polite" aria-busy="true">
    <div class="spinner"></div>
  </div>

  <div *ngIf="errorMessage" class="inline-error" role="status" aria-live="polite">
    <p class="body">{{ errorMessage }}</p>
    <button type="button" class="icon-btn" (click)="errorMessage = null" aria-label="Close">×</button>
  </div>

  <main class="schedule-workspace">
    <aside class="schedule-list-panel">
      <div class="panel-head">
        <div>
          <h2>Schedules</h2>
        </div>

        <button type="button" class="primary-btn small" (click)="openCreateModal()" [disabled]="isSaving">
          + New
        </button>
      </div>

      <div class="schedule-search-shell">
        <div class="search-input-shell">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>

          <input
            type="text"
            [value]="scheduleSearch"
            (input)="onScheduleSearchChange($any($event.target).value)"
            placeholder="Search schedules..."
            aria-label="Search schedules"
          />

          <button
            type="button"
            class="search-clear-btn"
            *ngIf="scheduleSearch"
            (click)="clearScheduleSearch()"
            aria-label="Clear schedule search"
          >
            ×
          </button>
        </div>
      </div>

      <div class="list-loading" *ngIf="isLoadingList">
        <div class="mini-spinner"></div>
      </div>

      <ng-container *ngIf="!isLoadingList">
        <ng-container *ngIf="scheduleGroups$ | async as groups">
          <div class="schedule-list" *ngIf="groups.length; else noGroups">
            <button
              type="button"
              class="schedule-card"
              *ngFor="let g of groups; let i = index"
              [ngClass]="getScheduleGroupColorClass(g)"
              [class.selected]="g.id === selectedScheduleGroupId"
              (click)="openScheduleGroup(g.id)"
            >
              <span class="schedule-dot" aria-hidden="true"></span>

              <span class="schedule-card-main">
                <strong>{{ g.name }}</strong>

                <span class="schedule-card-meta">
                  <span class="dept-badge" [ngClass]="getDepartmentColorClassById(g.primaryDepartmentId)">
                    {{ g.departmentLabel || 'Department' }}
                  </span>
                  <small>{{ g.createdAtLabel }}</small>
                </span>
              </span>
            </button>
          </div>

          <ng-template #noGroups>
            <div class="empty-panel">No schedules found.</div>
          </ng-template>
        </ng-container>
      </ng-container>

      <div class="list-pager">
        <span>Page {{ currentPage }} of {{ totalPages }}</span>

        <div>
          <button
            type="button"
            class="pager-btn"
            (click)="goToPage(currentPage - 1)"
            [disabled]="isLoadingList || currentPage <= 1"
            aria-label="Previous page"
          >
            ‹
          </button>

          <button
            type="button"
            class="pager-btn"
            (click)="goToPage(currentPage + 1)"
            [disabled]="isLoadingList || currentPage >= totalPages"
            aria-label="Next page"
          >
            ›
          </button>
        </div>

        <label class="page-size-select">
          <span>Size</span>
          <select
            [value]="pageSize"
            (change)="changePageSize($any($event.target).value)"
            [disabled]="isLoadingList"
            aria-label="Page size"
          >
            <option *ngFor="let s of pageSizeOptions" [value]="s">{{ s }}</option>
          </select>
        </label>
      </div>
    </aside>

    <section class="schedule-detail-panel">
      <div class="detail-loading" *ngIf="isLoadingDetails">
        <div class="mini-spinner"></div>
      </div>

      <ng-container *ngIf="!isLoadingDetails">
        <ng-container *ngIf="selectedScheduleGroupDetails as details; else detailsEmpty">
          <header class="detail-header-card" [ngClass]="selectedScheduleColorClass">
            <div class="detail-title-row">
              <span class="schedule-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4" />
                  <path d="M8 2v4" />
                  <path d="M3 10h18" />
                </svg>
              </span>

              <div class="detail-title-main">
                <h2>{{ details.name }}</h2>

                <div class="detail-badges">
                  <span
                    class="dept-badge"
                    *ngIf="details.departments?.length"
                    [ngClass]="getDepartmentColorClassById(details.departments[0]?.id)"
                  >
                    {{ details.departments[0]?.name || details.departments[0]?.code }}
                  </span>
                </div>
              </div>
            </div>

            <div class="detail-actions">
              <button type="button" class="outline-btn small" (click)="openRenameModal()" [disabled]="isSaving">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
                Rename
              </button>

              <button type="button" class="primary-btn small" (click)="openAssignModal()" [disabled]="isSaving">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M19 8v6" />
                  <path d="M22 11h-6" />
                </svg>
                Assign Schedule
              </button>
            </div>
          </header>

          <div class="detail-scroll-content">
            <section class="detail-section work-days-section">
              <div class="section-title-row">
                <h3>Work Days & Hours</h3>
                <span>{{ selectedDayRows.length }} days</span>
              </div>

              <div class="work-days-table" *ngIf="selectedDayRows.length; else noSelectedDays">
                <div class="work-days-head">
                  <span>Day</span>
                  <span>Start</span>
                  <span>End</span>
                  <span>Duration</span>
                  <span></span>
                </div>

                <div class="work-day-row" *ngFor="let r of selectedDayRows">
                  <strong>{{ r.dayOfWeek }}</strong>

                  <span class="time-pill">{{ formatScheduleDisplayTime(r.startTime) }}</span>
                  <span class="time-pill">{{ formatScheduleDisplayTime(r.endTime) }}</span>

                  <span class="duration-pill">
                    {{ getScheduleDuration(r.startTime, r.endTime) }}
                  </span>

                  <button
                    type="button"
                    class="icon-danger-btn"
                    (click)="requestRemoveDay(r.id)"
                    [disabled]="isSaving"
                    aria-label="Remove day"
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M3 6h18" />
                      <path d="M8 6V4h8v2" />
                      <path d="M19 6l-1 14H6L5 6" />
                    </svg>
                  </button>
                </div>
              </div>

              <ng-template #noSelectedDays>
                <div class="empty-panel">No days set on this schedule.</div>
              </ng-template>
            </section>

            <section class="detail-section assigned-section">
              <div class="section-title-row">
                <h3>Assigned Employees</h3>

                <span>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                  </svg>
                  {{ getEmployeeCount() }}
                </span>
              </div>

              <div class="assigned-search-shell">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>

                <input
                  type="text"
                  [value]="employeeSearch"
                  (input)="employeeSearch = $any($event.target).value"
                  placeholder="Search assigned employees..."
                  aria-label="Search assigned employees"
                />

                <button
                  type="button"
                  *ngIf="employeeSearch.trim()"
                  (click)="employeeSearch = ''"
                  aria-label="Clear employee search"
                >
                  ×
                </button>
              </div>

              <div class="department-employee-groups" *ngIf="details?.departments?.length; else noEmployeesAssigned">
                @for (d of details.departments; track d.id) {
                  <article class="department-card" [ngClass]="getDepartmentColorClassById(d.id)">
                    <button
                      type="button"
                      class="department-card-head"
                      [class.open]="isDepartmentOpen(d.id)"
                      (click)="toggleDepartment(d.id)"
                    >
                      <span class="dept-head-left">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M3 21h18" />
                          <path d="M5 21V7l8-4v18" />
                          <path d="M19 21V11l-6-4" />
                        </svg>

                        <strong>{{ d.name }}</strong>
                        <small *ngIf="d.code">{{ d.code }}</small>
                      </span>

                      <span class="dept-head-right">
                        {{ d.employees.length || 0 }} employees
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </span>
                    </button>

                    <div class="employee-card-list" *ngIf="isDepartmentOpen(d.id)">
                      <ng-container *ngIf="d.employees.length; else noDeptEmployees">
                        @for (e of d.employees; track e.id) {
                          <div
                            class="assigned-employee-row"
                            [ngClass]="getEmployeeColorClassById(e.id, e.employeeNumber)"
                          >
                            <span class="emp-avatar" [ngClass]="getEmployeeColorClassById(e.id, e.employeeNumber)">
                              {{ employeeInitials(e) }}
                            </span>

                            <span class="emp-main">
                              <strong>{{ e.firstName }} {{ e.lastName }}</strong>
                              <small *ngIf="e.employeeNumber">Employee #{{ e.employeeNumber }}</small>
                            </span>

                            <span class="employee-tags">
                              <em class="dept-tag" [ngClass]="getDepartmentColorClassById(d.id)">
                                {{ d.code || d.name }}
                              </em>
                              <em
                                class="company-tag"
                                *ngIf="$any(e).company?.id"
                                [ngClass]="getCompanyColorClassById($any(e).company?.id)"
                              >
                                {{ $any(e).company?.name }}
                              </em>
                            </span>
                          </div>
                        }
                      </ng-container>

                      <ng-template #noDeptEmployees>
                        <div class="empty-panel">No employees in this department.</div>
                      </ng-template>
                    </div>
                  </article>
                }
              </div>

              <ng-template #noEmployeesAssigned>
                <div class="assigned-empty">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                  </svg>
                  <p>No employees assigned.</p>
                  <small>Use Assign Schedule to add employees.</small>
                </div>
              </ng-template>
            </section>
          </div>
        </ng-container>

        <ng-template #detailsEmpty>
          <div class="detail-empty-state">
            <span class="empty-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4" />
                <path d="M8 2v4" />
                <path d="M3 10h18" />
              </svg>
            </span>
            <p>Select a schedule</p>
            <small>Choose a schedule from the left panel to view details.</small>
          </div>
        </ng-template>
      </ng-container>
    </section>
  </main>

  <div class="modal-backdrop" *ngIf="isModalOpen" (click)="closeModal()"></div>

  <div
    class="modal modal-redesign"
    *ngIf="isModalOpen"
    (click)="$event.stopPropagation()"
    role="dialog"
    aria-modal="true"
  >
    <div class="modal-header">
      <div class="modal-title">
        <h3>
          {{
            modalMode === 'create'
              ? 'New Schedule'
              : modalMode === 'rename'
                ? 'Rename Schedule'
                : 'Assign Schedule'
          }}
        </h3>

        <p class="modal-sub" *ngIf="modalMode === 'create'">Create a default schedule group.</p>
        <p class="modal-sub" *ngIf="modalMode === 'rename'">
          Update the schedule name. This does not affect assigned employees.
        </p>
        <p class="modal-sub" *ngIf="modalMode === 'assign'">
          Select employees and assign them to the selected schedule.
        </p>
      </div>

      <button
        type="button"
        class="icon-close"
        (click)="closeModal()"
        [disabled]="isSaving || isRenaming || assignIsLoadingUsers"
        aria-label="Close"
      >
        ×
      </button>
    </div>

    <ng-container *ngIf="modalMode === 'create'">
      <div class="modal-body">
        <div class="create-form-card">
          <div class="field">
            <label class="field-label">Schedule name</label>
            <input
              type="text"
              [value]="createName"
              (input)="createName = $any($event.target).value"
              placeholder="e.g. Standard Weekday"
            />
          </div>

          <div class="field">
            <label class="field-label">Department</label>
            <select [value]="createDepartmentId ?? ''" (change)="onCreateDepartmentChange($any($event.target).value)">
              <option value="">Select department…</option>
              <option *ngFor="let d of filterService.filterValues?.departments" [value]="d.id">
                {{ d.code }} - {{ d.name }}
              </option>
            </select>
          </div>

          <div class="field">
            <label class="field-label">Shift</label>
            <select [value]="createShiftId ?? ''" (change)="onShiftChange($any($event.target).value)">
              <option value="">Select shift…</option>
              <option *ngFor="let s of filterService.filterValues?.shifts" [value]="s.id">
                {{ s.name }}
              </option>
            </select>
          </div>

          <div class="field">
            <label class="field-label">Time zone</label>
            <input type="text" [value]="createTimeZoneId" disabled />
          </div>
        </div>

        <div class="days-card">
          <div class="days-head">
            <div>
              <strong>Days & Times</strong>
              <span>Enable a day to edit the time range.</span>
            </div>
          </div>

          <div class="create-day-list">
            <div class="create-day-row" *ngFor="let d of createDays; let i = index" [class.is-disabled]="!d.enabled">
              <label class="day-switch-row">
                <input
                  type="checkbox"
                  [checked]="d.enabled"
                  (change)="onCreateDayEnabledChange(i, $any($event.target).checked)"
                />

                <span class="day-switch-track">
                  <span></span>
                </span>

                <strong>{{ d.dayOfWeek }}</strong>
              </label>

              <div class="day-times">
                <label>
                  <span>Start</span>
                  <input
                    type="time"
                    [value]="d.startTime.slice(0, 5)"
                    [disabled]="!d.enabled"
                    (input)="onCreateDayStartTimeChange(i, $any($event.target).value)"
                  />
                </label>

                <label>
                  <span>End</span>
                  <input
                    type="time"
                    [value]="d.endTime.slice(0, 5)"
                    [disabled]="!d.enabled"
                    (input)="onCreateDayEndTimeChange(i, $any($event.target).value)"
                  />
                </label>
              </div>
            </div>
          </div>

          <div class="days-empty" *ngIf="!hasAnyCreateDaysEnabled">
            Select at least one day to create a schedule.
          </div>
        </div>

        <div class="modal-footer sticky-footer">
          <button type="button" class="outline-btn" (click)="closeModal()" [disabled]="isSaving">Cancel</button>
          <button type="button" class="primary-btn" (click)="createScheduleGroup()" [disabled]="!canCreateSchedule">
            Create schedule
          </button>
        </div>
      </div>
    </ng-container>

    <ng-container *ngIf="modalMode === 'rename'">
      <div class="modal-body">
        <div class="rename-card">
          <div class="field">
            <label class="field-label">New name</label>
            <input
              type="text"
              [value]="renameValue"
              (input)="renameValue = $any($event.target).value"
              placeholder="Enter new schedule name"
            />
          </div>

          <div class="rename-meta">
            <span><strong>Current</strong> {{ renameOriginal || '—' }}</span>
            <span><strong>ID</strong> #{{ selectedScheduleGroupId || '—' }}</span>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="outline-btn" (click)="closeModal()" [disabled]="isRenaming">
            Cancel
          </button>

          <button
            type="button"
            class="primary-btn"
            (click)="requestRenameConfirm()"
            [disabled]="isRenaming || !renameValue.trim() || renameValue.trim() === (renameOriginal || '').trim()"
          >
            Save
          </button>
        </div>
      </div>
    </ng-container>

    <ng-container *ngIf="modalMode === 'assign'">
      <div class="modal-body assign-modal-body">
        <div class="assign-filters">
          <div class="field grow">
            <label class="field-label">Search</label>
            <input
              type="text"
              [value]="assignSearch"
              (input)="onAssignSearchChange($any($event.target).value)"
              placeholder="Search by name/employee #"
            />
          </div>

          <div class="field">
            <label class="field-label">Company</label>
            <select
              [value]="assignCompanyId ?? ''"
              (change)="assignCompanyId = ($any($event.target).value ? +$any($event.target).value : null); onAssignFilterChange()"
              [disabled]="assignIsLoadingFilters"
            >
              <option value="">All</option>
              <option *ngFor="let c of (assignFilters?.companies || [])" [value]="c.id">
                {{ c.name }}
              </option>
            </select>
          </div>

          <div class="field">
            <label class="field-label">Department</label>
            <select
              [value]="assignDepartmentFilterId ?? ''"
              (change)="assignDepartmentFilterId = ($any($event.target).value ? +$any($event.target).value : null); onAssignFilterChange()"
              [disabled]="assignIsLoadingFilters"
            >
              <option value="">All</option>
              <option *ngFor="let d of (assignFilters?.departments || [])" [value]="d.id">
                {{ d.code }} - {{ d.name }}
              </option>
            </select>
          </div>

          <div class="field">
            <label class="field-label">Worker Type</label>
            <select
              [value]="assignWorkerTypeId ?? ''"
              (change)="assignWorkerTypeId = ($any($event.target).value ? +$any($event.target).value : null); onAssignFilterChange()"
              [disabled]="assignIsLoadingFilters"
            >
              <option value="">All</option>
              <option *ngFor="let w of (assignFilters?.workerTypes || [])" [value]="w.id">
                {{ w.name }}
              </option>
            </select>
          </div>

          <div class="field">
            <label class="field-label">Shift</label>
            <select
              [value]="assignShiftId ?? ''"
              (change)="assignShiftId = ($any($event.target).value ? +$any($event.target).value : null); onAssignFilterChange()"
              [disabled]="assignIsLoadingFilters"
            >
              <option value="">All</option>
              <option *ngFor="let s of (assignFilters?.shifts || [])" [value]="s.id">
                {{ s.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="assignment-department-card">
          <div class="field grow">
            <label class="field-label">Department for this assignment <span class="req">*</span></label>

            <select
              [value]="assignDepartmentForScheduleId ?? ''"
              (change)="onAssignDepartmentForScheduleChange($any($event.target).value)"
              [disabled]="assignIsLoadingFilters"
            >
              <option value="">Select department…</option>
              <option *ngFor="let d of (assignFilters?.departments || [])" [value]="d.id">
                {{ d.code }} - {{ d.name }}
              </option>
            </select>

            <div class="hint" *ngIf="!assignDepartmentForScheduleId">
              This department is required and will be used when saving.
            </div>
          </div>
        </div>

        <div class="assign-toolbar">
          <div class="left">
            <span class="pill">Showing <strong>{{ assignUsers.length }}</strong> / <strong>{{ assignTotalCount }}</strong></span>
            <span class="pill selected">Selected <strong>{{ selectedAssignCount }}</strong></span>
          </div>

          <div class="right">
            <button type="button" class="outline-btn small" (click)="selectAllOnPage()" [disabled]="assignIsLoadingUsers || !assignUsers.length">
              Select page
            </button>
            <button type="button" class="outline-btn small" (click)="clearAllOnPage()" [disabled]="assignIsLoadingUsers || !assignUsers.length">
              Clear page
            </button>
            <button type="button" class="outline-btn small" (click)="clearAllSelected()" [disabled]="assignIsLoadingUsers || selectedAssignCount === 0">
              Clear all
            </button>
          </div>
        </div>

        <div class="assign-list" aria-live="polite">
          <div class="mini-spinner" *ngIf="assignIsLoadingUsers"></div>

          <ng-container *ngIf="!assignIsLoadingUsers">
            <div class="assign-empty" *ngIf="!assignUsers.length">
              No employees found for the current filters.
            </div>

            <div
              class="assign-row"
              *ngFor="let u of assignUsers"
              [ngClass]="getEmployeeColorClassById(u.id, u.employeeNumber)"
            >
              <label class="assign-check">
                <input
                  type="checkbox"
                  [checked]="isEmployeeSelected(u.id)"
                  (change)="toggleEmployeeSelected(u.id, $any($event.target).checked)"
                />
              </label>

              <span class="assign-avatar" [ngClass]="getEmployeeColorClassById(u.id, u.employeeNumber)">
                {{ employeeInitials(u) }}
              </span>

              <div class="assign-main">
                <strong>{{ (u.firstName || '') + ' ' + (u.lastName || '') }}</strong>
                <small>
                  <ng-container *ngIf="u.employeeNumber">#{{ u.employeeNumber }}</ng-container>
                  <ng-container *ngIf="u.department"> • {{ u.department.name }}</ng-container>
                  <ng-container *ngIf="$any(u).company"> • {{ $any(u).company.name }}</ng-container>
                  • Current schedule: {{ u.scheduleGroup?.name || '—' }}
                </small>

                <span class="employee-tags assign-user-tags">
                  <em
                    class="dept-tag"
                    *ngIf="u.department"
                    [ngClass]="getDepartmentColorClassById(u.department?.id)"
                  >
                    {{ u.department?.code || u.department?.name }}
                  </em>

                  <em
                    class="company-tag"
                    *ngIf="$any(u).company"
                    [ngClass]="getCompanyColorClassById($any(u).company?.id)"
                  >
                    {{ $any(u).company?.name }}
                  </em>
                </span>
              </div>
            </div>
          </ng-container>
        </div>

        <div class="assign-pager" *ngIf="assignTotalPages > 1">
          <button type="button" class="outline-btn small" (click)="goToAssignPage(assignPage - 1)" [disabled]="assignIsLoadingUsers || assignPage <= 1">
            ← Prev
          </button>

          <div class="page-meta">Page {{ assignPage }} / {{ assignTotalPages }}</div>

          <button type="button" class="outline-btn small" (click)="goToAssignPage(assignPage + 1)" [disabled]="assignIsLoadingUsers || assignPage >= assignTotalPages">
            Next →
          </button>
        </div>

        <div class="modal-footer sticky-footer">
          <button type="button" class="outline-btn" (click)="closeModal()" [disabled]="isSaving">Cancel</button>
          <button type="button" class="primary-btn" (click)="requestAssignConfirm()" [disabled]="isAssignSaveDisabled">
            Assign selected
          </button>
        </div>
      </div>
    </ng-container>
  </div>

  <div class="confirm-backdrop" *ngIf="confirmOpen" (click)="onConfirmCancel()"></div>

  <div class="confirm-modal" *ngIf="confirmOpen" (click)="$event.stopPropagation()" role="dialog" aria-modal="true">
    <div class="confirm-header">
      <h3 class="confirm-title">{{ confirmTitle }}</h3>
      <button type="button" class="outline-btn small" (click)="onConfirmCancel()" aria-label="Close">Close</button>
    </div>

    <div class="confirm-body">
      <div class="confirm-message">{{ confirmMessage }}</div>
    </div>

    <div class="confirm-actions">
      <button type="button" class="outline-btn" (click)="onConfirmCancel()">{{ confirmCancelText }}</button>
      <button
        type="button"
        [class.primary-btn]="confirmKind === 'default'"
        [class.danger-btn]="confirmKind === 'danger'"
        (click)="onConfirmOk()"
      >
        {{ confirmOkText }}
      </button>
    </div>
  </div>
</div>